import type { ContextConfig, ContextVariableType } from './contextConfig.ts';

/**
 * Helpers of the Context step preview (BB-119): example data for `POST …/canvas-steps/context/preview` taken from the
 * step and the graph, and the server's `notUpdated` reasons in words.
 */

/** A value of each type that converts as that type (`coerceContextValue`), for the example data. */
export const CONTEXT_SAMPLES: Readonly<Record<ContextVariableType, unknown>> = {
  boolean: true,
  number: 100,
  string: 'text',
  array: [],
  object: {},
  time: '2026-01-01T09:00:00Z',
};

const sample = (type: ContextVariableType) => structuredClone(CONTEXT_SAMPLES[type]);
const isObject = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);

/** `event_properties.${a}.b`, `event_properties.a.b`: the property and the path below it. */
const EVENT_REFERENCE = /event_properties\.(?:\$\{(\w+)\}|([A-Za-z_]\w*))((?:\.[A-Za-z_]\w*)*)/g;
/** `context.${a}`, `context.a`: the variable. */
const CONTEXT_REFERENCE = /context\.(?:\$\{(\w+)\}|([A-Za-z_]\w*))/g;

/** Sets `value` at `path`, turning a leaf on the way into an object (a deeper path wins over a shorter one). */
function setPath(target: Record<string, unknown>, path: string[], value: unknown) {
  let at = target;
  for (const [i, key] of path.entries()) {
    if (i === path.length - 1) {
      if (!(key in at)) at[key] = value;
      return;
    }
    if (!isObject(at[key])) at[key] = {};
    at = at[key] as Record<string, unknown>;
  }
}

/** Example event properties: every `event_properties.*` path the values read, with a sample of the reading variable's type. */
export function exampleEvent(config: ContextConfig): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const v of config.variables) {
    for (const m of v.expression.matchAll(EVENT_REFERENCE)) {
      const path = [m[1] ?? m[2]!, ...(m[3] ? m[3].slice(1).split('.') : [])];
      setPath(out, path, sample(v.type));
    }
  }
  return out;
}

/**
 * Example context before the step: what earlier Context steps set (a sample of their type), and what the values read
 * from `context.*` that neither they nor an earlier variable of this step set — the entry's own context (API trigger
 * properties), with a sample of the reading variable's type.
 */
export function exampleContext(config: ContextConfig, earlier: ReadonlyArray<{ name: string; types: readonly ContextVariableType[] }>): Record<string, unknown> {
  const out: Record<string, unknown> = Object.fromEntries(earlier.map((v) => [v.name, sample(v.types[0] ?? 'string')]));
  for (const [i, v] of config.variables.entries()) {
    const setBefore = new Set(config.variables.slice(0, i).map((x) => x.name));
    for (const m of v.expression.matchAll(CONTEXT_REFERENCE)) {
      const name = m[1] ?? m[2]!;
      if (!(name in out) && !setBefore.has(name)) out[name] = sample(v.type);
    }
  }
  return out;
}

/** An example as the JSON field shows it; nothing to show is an empty field. */
export const exampleText = (value: Record<string, unknown>) => (Object.keys(value).length ? JSON.stringify(value, null, 2) : '');

const REASONS: Record<string, string> = {
  aborted: 'abort_message stopped it',
  not_a_boolean: 'the value is not true or false',
  not_a_number: 'the value is not a number',
  not_a_time: 'the value is not a date or time',
  not_an_array: 'the value is not a JSON array',
  not_an_object: 'the value is not a JSON object',
  context_too_large: 'the context would grow past 50 KB',
};

/** Why a variable was not updated (`notUpdated[].reason` of the preview and the trace), in words. */
export function notUpdatedReason(reason: string): string {
  if (REASONS[reason]) return REASONS[reason];
  if (reason.startsWith('template_error:')) return `Liquid error: ${reason.slice('template_error:'.length).trim()}`;
  return `it could not be worked out (${reason})`;
}

/** A context value as the preview shows it: JSON, so that `100` and `"100"` differ. */
export const contextValueText = (value: unknown) => JSON.stringify(value) ?? String(value);
